import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { toast } from 'react-toastify';
import styles from '../Announcements.module.scss';
import Buttons from '../../../Atoms/Buttons/Buttons';
import Loader from '../../../Molecules/Loader/Loader';
import LoaderButton from '../../../Molecules/Loader/LoaderButton';
import { useToast } from '../../../Molecules/Custom/CustomToaster/Toast';
import {
  useFetchAnnouncementById,
  useCreateAnnouncement,
  useUpdateAnnouncement,
  useUploadAnnouncementImage,
} from '../../../../Hooks/Announcement';
import { API_URL } from '../../../../Constants';

/**
 * إنشاء / تعديل إعلان — one component for both, because the entity is
 * identical in the two modes and a second component would duplicate the
 * validation and then drift from it. The route decides: `:id` present means
 * edit.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * ARABIC ONLY — THIS IS NOT AN OVERSIGHT
 * ────────────────────────────────────────────────────────────────────────────
 *
 * There is no English field anywhere on this form, and there must not be. The
 * public website is authored in Arabic and translated AT RUNTIME by the Google
 * Translate service it already loads; `News`, `Degree`, `Testimonial` and every
 * other content controller return a single Arabic field per item, and the
 * announcement is not an exception. Decision recorded 2026-10-05 in
 * DPA-FE `docs/Announcements&visitors/01-development-roadmap.md` §3.1.
 *
 * ONE CONSEQUENCE AN OPERATOR NEEDS TO KNOW, and it is on the form: text baked
 * into a banner IMAGE is pixels, so the translate service cannot reach it. An
 * Arabic banner stays Arabic for an English reader. Essential wording belongs
 * in the title and body, where it translates; the image is decoration.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * DATES ARE THE EASIEST THING ON THIS SCREEN TO GET SILENTLY WRONG
 * ────────────────────────────────────────────────────────────────────────────
 *
 * `<input type="datetime-local">` has NO time zone. It reads and writes local
 * wall-clock time, and the API stores and compares UTC. The conversion happens
 * in exactly two places — `toLocalInput` below, and `toAnnouncementPayload` in
 * `Hooks/Announcement.js` — and nowhere else. Getting it wrong is a four-hour
 * scheduling error on a feature whose entire purpose is scheduling, and it
 * would not announce itself: the record would simply go live at the wrong time.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * VALIDATION IS CLIENT-FIRST, SERVER-AUTHORITATIVE
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Every rule below mirrors one the backend enforces (§A4), so the operator is
 * told in the field rather than after a round trip. The server still decides;
 * `serverErrors` places whatever it sends under the field it belongs to. The
 * client rules are never STRICTER than the server's — a client rejecting what
 * the backend would have accepted is a bug the operator cannot work around.
 */

/**
 * A deliberately narrow toolbar.
 *
 * `quillModules` in `Constants.js` offers headers, fonts, sizes, images and
 * video. In a pop-up that is at most ~640px wide on a desktop and ~90% of a
 * phone, an embedded video or a second heading level is a layout problem, not a
 * feature — and the website sanitises the HTML on the way in, so anything
 * fancy may simply vanish. Bold, italic, a list and a link is what a short
 * notice needs.
 */
const BODY_MODULES = {
  toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link'], ['clean']],
  clipboard: { matchVisual: false },
};

const BODY_FORMATS = ['bold', 'italic', 'underline', 'list', 'bullet', 'link'];

const LAYOUTS = [
  { value: 'TextOnly', label: 'نص فقط' },
  { value: 'ImageOnly', label: 'صورة فقط' },
  { value: 'ImageAndText', label: 'صورة ونص' },
];

const FREQUENCIES = [
  {
    value: 'EveryVisit',
    label: 'في كل زيارة',
    hint: 'يظهر عند كل فتح للصفحة. استخدمه للحالات العاجلة فقط.',
  },
  {
    value: 'OncePerSession',
    label: 'مرة واحدة لكل جلسة',
    hint: 'يظهر مرة واحدة لكل تبويب متصفح، ويعود للظهور في جلسة جديدة.',
  },
  {
    value: 'OncePerDevice',
    label: 'مرة واحدة لكل جهاز',
    hint: 'يظهر مرة واحدة فقط لكل متصفح — حتى يُعدَّل محتوى الإعلان.',
  },
  {
    value: 'EveryNDays',
    label: 'كل عدة أيام',
    hint: 'يعود للظهور بعد انقضاء المدة المحددة. مناسب للإعلانات طويلة الأمد.',
  },
];

/** Max 2 MB, JPG/PNG/WebP, at most 4096px a side — the server's own limits. */
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_IMAGE_SIDE = 4096;

/**
 * The server's field limits, mirrored (FRONTEND-NOTES.md, 2026-10-06).
 *
 * Mirrored, NOT invented: every number here is one the backend enforces. The
 * point is to tell the operator in the field while they are typing, rather than
 * rejecting 600 words of Arabic after a round trip. The server remains the
 * authority — and because these match it exactly, the client never refuses
 * something the API would have accepted.
 */
const LIMITS = {
  title: 200,
  body: 20000,
  imageAlt: 300,
  ctaLabel: 100,
  ctaUrl: 500,
  priorityMin: 0,
  priorityMax: 1000,
  frequencyDaysMin: 1,
  frequencyDaysMax: 365,
};

/** Characters in rich text, ignoring the markup the limit does not count. */
const textLength = (html) =>
  String(html ?? '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ').length;

/**
 * ISO-8601 UTC -> the `YYYY-MM-DDTHH:mm` an `<input type="datetime-local">`
 * wants, expressed in the OPERATOR's own zone.
 *
 * `toISOString().slice(0,16)` is the tempting one-liner and it is wrong: it
 * renders UTC into a field the browser will read back as local, shifting every
 * stored time by the offset the moment the record is re-saved.
 */
const toLocalInput = (iso) => {
  if (!iso) return '';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '';

  const pad = (value) => String(value).padStart(2, '0');
  return [
    parsed.getFullYear(),
    '-',
    pad(parsed.getMonth() + 1),
    '-',
    pad(parsed.getDate()),
    'T',
    pad(parsed.getHours()),
    ':',
    pad(parsed.getMinutes()),
  ].join('');
};

/** `new Date()` rounded to the minute, for a new record's default start. */
const nowLocalInput = () => toLocalInput(new Date().toISOString());

/** Quill writes `<p><br></p>` for "empty", which is not empty to `trim()`. */
const isBlankHtml = (html) => {
  if (!html) return true;
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() === '';
};

const EMPTY = {
  title: '',
  body: '',
  imageUrl: '',
  imageAlt: '',
  ctaLabel: '',
  ctaUrl: '',
  ctaOpensInNewTab: false,
  layout: 'TextOnly',
  displayFrequency: 'OncePerSession',
  frequencyDays: 7,
  startsOn: '',
  endsOn: '',
  priority: 0,
  isPublished: false,
};

/** The fields whose change makes the backend bump `version` (§A5). */
const CONTENT_FIELDS = [
  'title',
  'body',
  'imageUrl',
  'imageAlt',
  'ctaLabel',
  'ctaUrl',
  'layout',
];

/**
 * ────────────────────────────────────────────────────────────────────────────
 * «تعبئة سريعة» — five ready-made announcements
 * ────────────────────────────────────────────────────────────────────────────
 *
 * They are real, usable Academy notices, not lorem ipsum — an operator can
 * publish one after changing a date. But they were CHOSEN as a set, so that
 * clicking through all five exercises every branch of the feature exactly once:
 *
 *   #   layout         frequency        CTA              also covers
 *   1   TextOnly       OncePerSession   labelled         the ordinary case
 *   2   ImageOnly      OncePerDevice    NO label         the whole banner is
 *                                                        the link — the case
 *                                                        that broke once
 *   3   ImageAndText   EveryNDays (7)   labelled, new tab external CTA
 *   4   TextOnly       EveryVisit       none             no button at all
 *   5   ImageAndText   OncePerDevice    labelled         long title + long body
 *                                                        -> internal scrolling
 *
 * CREATE ONLY. On the edit screen this panel is not rendered: overwriting a
 * live record's content with a sample, in one click, is not a convenience.
 *
 * NO `imageUrl`. The API only accepts a URL that `UploadImage` returned, so a
 * hard-coded one would be rejected (`ANNOUNCEMENT_IMAGE_INVALID`). The two
 * image presets deliberately leave it empty and the form's own validation then
 * asks for the upload — which is the intended flow, not a gap.
 */
const QUICK_FILL = [
  {
    key: 'exams',
    label: 'تأجيل اختبارات',
    note: 'نص فقط · مرة لكل جلسة · زر داخلي',
    values: {
      title: 'تأجيل موعد الاختبارات النهائية',
      body:
        '<p>تم تأجيل موعد الاختبارات النهائية لجميع البرامج الأكاديمية إلى الأسبوع التالي.' +
        ' يُرجى مراجعة التقويم الأكاديمي للاطلاع على المواعيد المحدّثة.</p>',
      imageAlt: '',
      ctaLabel: 'عرض التقويم الأكاديمي',
      ctaUrl: '/academic-calendar',
      ctaOpensInNewTab: false,
      layout: 'TextOnly',
      displayFrequency: 'OncePerSession',
      frequencyDays: 7,
      priority: 20,
      days: 14,
    },
  },
  {
    key: 'admission-banner',
    label: 'لافتة قبول (صورة قابلة للنقر)',
    note: 'صورة فقط · مرة لكل جهاز · بدون نص زر — الصورة كلها رابط',
    values: {
      title: '',
      body: '',
      imageAlt: 'لافتة فتح باب القبول للعام الأكاديمي الجديد',
      // Left empty on purpose: with ImageOnly the API accepts a bare ctaUrl and
      // the whole banner becomes the link.
      ctaLabel: '',
      ctaUrl: '/admissions/overview',
      ctaOpensInNewTab: false,
      layout: 'ImageOnly',
      displayFrequency: 'OncePerDevice',
      frequencyDays: 7,
      priority: 30,
      days: 30,
    },
  },
  {
    key: 'conference',
    label: 'مؤتمر (صورة ونص)',
    note: 'صورة ونص · كل 7 أيام · زر خارجي بتبويب جديد',
    values: {
      title: 'المؤتمر السنوي للعلوم الشرطية',
      body:
        '<p>تستضيف الأكاديمية المؤتمر السنوي للعلوم الشرطية بمشاركة نخبة من الباحثين.</p>' +
        '<p>التسجيل مفتوح حتى نهاية الشهر الجاري.</p>',
      imageAlt: 'صورة من المؤتمر السنوي للعلوم الشرطية',
      ctaLabel: 'التسجيل في المؤتمر',
      ctaUrl: 'https://www.dubaipolice.gov.ae',
      ctaOpensInNewTab: true,
      layout: 'ImageAndText',
      displayFrequency: 'EveryNDays',
      frequencyDays: 7,
      priority: 10,
      days: 21,
    },
  },
  {
    key: 'maintenance',
    label: 'إشعار صيانة (بدون زر)',
    note: 'نص فقط · في كل زيارة · بدون زر',
    values: {
      title: 'أعمال صيانة على البوابة الإلكترونية',
      body:
        '<p>ستجرى أعمال صيانة على البوابة الإلكترونية يوم الجمعة من الساعة الثانية' +
        ' حتى الرابعة صباحًا. قد تتعذّر بعض الخدمات خلال هذه الفترة.</p>',
      imageAlt: '',
      ctaLabel: '',
      ctaUrl: '',
      ctaOpensInNewTab: false,
      layout: 'TextOnly',
      displayFrequency: 'EveryVisit',
      frequencyDays: 7,
      priority: 0,
      days: 3,
    },
  },
  {
    key: 'long',
    label: 'إعلان طويل (اختبار التمرير)',
    note: 'صورة ونص · عنوان ونص طويلان — لفحص التمرير داخل النافذة',
    values: {
      title:
        'تعليمات مهمة بشأن إجراءات القبول والتسجيل للعام الأكاديمي الجديد لجميع' +
        ' البرامج الجامعية وبرامج الدراسات العليا',
      body:
        '<p>يُرجى من جميع الطلبة المتقدمين الاطلاع على التعليمات التالية قبل استكمال' +
        ' إجراءات التسجيل:</p>' +
        '<ul><li>استكمال جميع الوثائق المطلوبة قبل الموعد النهائي.</li>' +
        '<li>التأكد من صحة البيانات الشخصية المدخلة في الطلب.</li>' +
        '<li>مراجعة شروط القبول الخاصة بكل برنامج أكاديمي على حدة.</li>' +
        '<li>متابعة البريد الإلكتروني لاستلام إشعارات حالة الطلب.</li></ul>' +
        '<p>لن يُنظر في الطلبات غير المستكملة بعد انتهاء المدة المحددة، ويمكن' +
        ' التواصل مع قسم القبول والتسجيل للاستفسار عن أي من البنود أعلاه.</p>' +
        '<p>نتمنى لجميع الطلبة التوفيق والنجاح.</p>',
      imageAlt: 'صورة لمبنى الأكاديمية',
      ctaLabel: 'إجراءات القبول',
      ctaUrl: '/admissions/overview',
      ctaOpensInNewTab: false,
      layout: 'ImageAndText',
      displayFrequency: 'OncePerDevice',
      frequencyDays: 7,
      priority: 5,
      days: 45,
    },
  },
];

/** `days` from now, as a `datetime-local` string. */
const inDays = (days) => {
  const when = new Date();
  when.setDate(when.getDate() + days);
  return toLocalInput(when.toISOString());
};

const AnnouncementForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const customizedToast = useToast();
  const fileInputRef = useRef(null);

  const [values, setValues] = useState(() => ({
    ...EMPTY,
    startsOn: nowLocalInput(),
  }));
  /** What the record looked like when it was loaded — for the version warning. */
  const [original, setOriginal] = useState(null);
  const [touched, setTouched] = useState(false);
  const [serverError, setServerError] = useState({ errors: null, message: '' });

  const {
    data: existing,
    isLoading: isLoadingRecord,
    isError: isLoadError,
  } = useFetchAnnouncementById(id, { enabled: isEdit });

  const { mutate: create, isLoading: isCreating } = useCreateAnnouncement();
  const { mutate: update, isLoading: isUpdating } = useUpdateAnnouncement();
  const { mutate: uploadImage, isLoading: isUploading } =
    useUploadAnnouncementImage();

  useEffect(() => {
    if (!existing) return;

    const loaded = {
      title: existing.title ?? '',
      body: existing.body ?? '',
      imageUrl: existing.imageUrl ?? '',
      imageAlt: existing.imageAlt ?? '',
      ctaLabel: existing.ctaLabel ?? '',
      ctaUrl: existing.ctaUrl ?? '',
      ctaOpensInNewTab: existing.ctaOpensInNewTab === true,
      layout: existing.layout ?? 'TextOnly',
      displayFrequency: existing.displayFrequency ?? 'OncePerSession',
      frequencyDays: existing.frequencyDays ?? 7,
      startsOn: toLocalInput(existing.startsOn),
      endsOn: toLocalInput(existing.endsOn),
      priority: existing.priority ?? 0,
      isPublished: existing.isPublished === true,
    };

    setValues(loaded);
    setOriginal(loaded);
  }, [existing]);

  const setField = (patch) => setValues((current) => ({ ...current, ...patch }));

  const needsTitle = values.layout !== 'ImageOnly';
  const needsImage = values.layout !== 'TextOnly';

  const errors = useMemo(() => {
    const next = {};

    if (needsTitle && !values.title.trim()) next.title = 'العنوان مطلوب';
    else if (values.title.trim().length > LIMITS.title)
      next.title = `الحد الأقصى ${LIMITS.title} حرفًا`;

    if (textLength(values.body) > LIMITS.body)
      next.body = `الحد الأقصى ${LIMITS.body} حرفًا`;

    if (values.layout === 'ImageOnly' && !values.imageUrl.trim())
      next.imageUrl = 'الصورة مطلوبة لهذا التنسيق';
    // Accessibility, and the backend rejects it too: an image with no alternative
    // text is unreadable to a screen reader and invisible when it fails to load.
    if (values.imageUrl.trim() && !values.imageAlt.trim())
      next.imageAlt = 'النص البديل مطلوب عند وجود صورة';
    else if (values.imageAlt.trim().length > LIMITS.imageAlt)
      next.imageAlt = `الحد الأقصى ${LIMITS.imageAlt} حرفًا`;

    if (!values.startsOn) next.startsOn = 'تاريخ البدء مطلوب';

    if (values.startsOn && values.endsOn) {
      const starts = new Date(values.startsOn).getTime();
      const ends = new Date(values.endsOn).getTime();
      if (Number.isFinite(starts) && Number.isFinite(ends) && ends <= starts)
        next.endsOn = 'تاريخ الانتهاء يجب أن يكون بعد تاريخ البدء';
    }

    if (values.displayFrequency === 'EveryNDays') {
      const days = Number(values.frequencyDays);
      if (!Number.isFinite(days) || days < LIMITS.frequencyDaysMin)
        next.frequencyDays = `أدخل عدد أيام لا يقل عن ${LIMITS.frequencyDaysMin}`;
      else if (days > LIMITS.frequencyDaysMax)
        next.frequencyDays = `الحد الأقصى ${LIMITS.frequencyDaysMax} يومًا`;
    }

    const priority = Number(values.priority);
    if (
      !Number.isFinite(priority) ||
      priority < LIMITS.priorityMin ||
      priority > LIMITS.priorityMax
    )
      next.priority = `الأولوية بين ${LIMITS.priorityMin} و${LIMITS.priorityMax}`;

    /* A destination with no label is normally a button nobody can read — EXCEPT
     * for `ImageOnly`, where the backend deliberately allows a bare `ctaUrl`
     * and the whole banner becomes the link. Requiring a label there would
     * block a case the API supports. */
    if (
      values.ctaUrl.trim() &&
      !values.ctaLabel.trim() &&
      values.layout !== 'ImageOnly'
    )
      next.ctaLabel = 'نص الزر مطلوب عند تحديد رابط';
    else if (values.ctaLabel.trim().length > LIMITS.ctaLabel)
      next.ctaLabel = `الحد الأقصى ${LIMITS.ctaLabel} حرفًا`;

    // And a label with no destination is a button that does nothing.
    if (values.ctaLabel.trim() && !values.ctaUrl.trim())
      next.ctaUrl = 'الرابط مطلوب عند تحديد نص للزر';

    if (values.ctaUrl.trim()) {
      const url = values.ctaUrl.trim();
      /* The backend's exact rule: ONE leading slash, or an absolute http(s)
         URL. `//host` is a protocol-relative link to somewhere else wearing
         the costume of a path, and spaces and backslashes are rejected
         outright — `ANNOUNCEMENT_CTA_URL_INVALID`. */
      const isInternal = url.startsWith('/') && !url.startsWith('//');
      const isHttp = /^https?:\/\//i.test(url);

      if (url.length > LIMITS.ctaUrl) {
        next.ctaUrl = `الحد الأقصى ${LIMITS.ctaUrl} حرفًا`;
      } else if (/[\s\\]/.test(url)) {
        next.ctaUrl = 'الرابط لا يقبل مسافات أو شرطة مائلة عكسية';
      } else if (url.startsWith('//')) {
        next.ctaUrl = 'ابدأ المسار بشرطة مائلة واحدة، أو استخدم رابطًا كاملاً بـ https://';
      } else if (!isInternal && !isHttp) {
        next.ctaUrl = 'أدخل مسارًا يبدأ بـ / أو رابطًا يبدأ بـ http(s)://';
      } else if (isHttp) {
        try {
          // The CTA points a VISITOR somewhere. The API host serves JSON, not
          // pages — a link to it is always a mistake, and the backend rejects
          // it as `ANNOUNCEMENT_CTA_HOST_INVALID`. Caught here so the operator
          // finds out while they are still looking at the field.
          const apiHost = new URL(API_URL).host;
          if (new URL(url).host === apiHost)
            next.ctaUrl = 'لا يمكن توجيه الزائر إلى عنوان واجهة البيانات (API).';
        } catch {
          next.ctaUrl = 'الرابط غير صالح';
        }
      }
    }

    return next;
  }, [values, needsTitle]);

  const errorFor = (field) =>
    (touched ? errors[field] : undefined) ??
    // The server keys its validation map by the request field name.
    serverError.errors?.[field]?.[0] ??
    serverError.errors?.[field.charAt(0).toUpperCase() + field.slice(1)]?.[0];

  /**
   * Will saving this re-show the announcement to people who already closed it?
   *
   * Shown BEFORE the save, not after, because it is the one consequence an
   * operator cannot undo and would not otherwise predict: a typo fix in the
   * title re-opens the dialog for every visitor who dismissed the old one,
   * while a change to the priority or the schedule does not.
   */
  const willResetDismissals = useMemo(() => {
    if (!isEdit || !original) return false;
    return CONTENT_FIELDS.some((field) => values[field] !== original[field]);
  }, [isEdit, original, values]);

  /**
   * Fill the form from one of the five samples.
   *
   * TWO THINGS IT DELIBERATELY KEEPS:
   *
   *  - ANY IMAGE ALREADY UPLOADED. An upload is a real round trip that cost the
   *    operator a file-picker and a wait; wiping it because they clicked a
   *    second sample to compare would be the one genuinely annoying thing this
   *    panel could do. The sample's own `imageAlt` only applies when there is
   *    no image yet, so an uploaded banner keeps the description written for it.
   *  - `isPublished`. Filling a form is not a decision to publish, and silently
   *    arming that switch from a sample is exactly how a test announcement
   *    reaches the public site.
   *
   * Dates are computed at CLICK time, not defined in the preset: a sample whose
   * window opened last Tuesday would be born `Expired`.
   */
  const applyQuickFill = (preset) => {
    const { days, ...rest } = preset.values;
    setValues((current) => ({
      ...current,
      ...rest,
      imageUrl: current.imageUrl,
      imageAlt: current.imageUrl ? current.imageAlt : rest.imageAlt,
      startsOn: nowLocalInput(),
      endsOn: inDays(days),
    }));
    /* Errors from a previous attempt describe fields that no longer hold those
     * values. Cleared so the operator is not shown a stale complaint. */
    setTouched(false);
    setServerError({ errors: null, message: '' });
  };

  const handleFile = (event) => {
    const file = event.target.files?.[0];
    // Always clear the input: picking the SAME file twice after a failed upload
    // fires no `change` event otherwise, and the operator thinks the button is
    // broken.
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;

    /* Checked by MIME type AND size before the request — the server validates
     * by content, which is the authoritative check, but there is no reason to
     * upload 8 MB to be told no. */
    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast.error('الصيغ المقبولة: JPG أو PNG أو WebP فقط.');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error('حجم الصورة يجب أن يكون أقل من 2 ميجابايت.');
      return;
    }

    /* The server caps each side at 4096px. Measured here first so an operator
     * who picked a print-resolution poster is told immediately, rather than
     * after uploading it. `createObjectURL` is revoked either way — a leaked
     * blob URL holds the whole file in memory for the life of the page. */
    const objectUrl = URL.createObjectURL(file);
    const probe = new Image();
    probe.onload = () => {
      const tooBig =
        probe.naturalWidth > MAX_IMAGE_SIDE || probe.naturalHeight > MAX_IMAGE_SIDE;
      URL.revokeObjectURL(objectUrl);
      if (tooBig) {
        toast.error(
          `أبعاد الصورة يجب ألا تتجاوز ${MAX_IMAGE_SIDE} بكسل في كل اتجاه.`
        );
        return;
      }
      send();
    };
    probe.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      /* Unreadable as an image here does not necessarily mean the server will
       * refuse it, and the server validates by CONTENT. Let it decide rather
       * than blocking a file it might accept. */
      send();
    };
    probe.src = objectUrl;

    function send() {
    uploadImage(file, {
      onSuccess: (result) => {
        const url = result?.url;
        if (!url) {
          toast.error('تم الرفع لكن لم يُرجع الخادم رابط الصورة.');
          return;
        }
        /* An image implies a layout that shows one. Silently leaving `TextOnly`
           selected after an upload means the banner is stored and never drawn,
           which looks like a bug in the website. */
        setField({
          imageUrl: url,
          layout: values.layout === 'TextOnly' ? 'ImageAndText' : values.layout,
        });
        customizedToast.open('تم رفع الصورة بنجاح');
      },
      onError: (err) => toast.error(err?.message ?? 'تعذّر رفع الصورة'),
    });
    }
  };

  const handleSubmit = (event) => {
    event?.preventDefault?.();
    setTouched(true);
    setServerError({ errors: null, message: '' });

    if (Object.keys(errors).length > 0) {
      toast.error('يرجى تصحيح الحقول المحددة قبل الحفظ.');
      return;
    }

    const onError = (err) => {
      setServerError({
        errors: err?.errors ?? null,
        // A banner only for something no field can carry on its own.
        message: err?.errors ? '' : (err?.message ?? 'تعذّر حفظ الإعلان'),
      });
    };

    if (isEdit) {
      update(
        { id, ...values },
        {
          onSuccess: () => {
            customizedToast.open(
              willResetDismissals
                ? 'تم حفظ التعديلات — سيظهر الإعلان من جديد لمن أغلقه سابقًا'
                : 'تم حفظ التعديلات بنجاح'
            );
            navigate('/home/announcements');
          },
          onError,
        }
      );
      return;
    }

    create(values, {
      onSuccess: () => {
        customizedToast.open('تم إنشاء الإعلان بنجاح');
        navigate('/home/announcements');
      },
      onError,
    });
  };

  if (isEdit && isLoadingRecord) {
    return (
      <div className={styles.page}>
        <Loader />
      </div>
    );
  }

  if (isEdit && isLoadError) {
    return (
      <div className={styles.page}>
        <p className={styles.empty}>تعذّر تحميل الإعلان. ربما تم حذفه.</p>
        <Buttons
          text='العودة إلى القائمة'
          rounded
          color='rgba(13, 160, 110, 1)'
          type='submit'
          width={200}
          style={{ flex: '0 0 auto' }}
          handleOnClick={() => navigate('/home/announcements')}
        />
      </div>
    );
  }

  const isSaving = isCreating || isUpdating;
  const selectedFrequency = FREQUENCIES.find(
    (item) => item.value === values.displayFrequency
  );

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div className={styles.heading}>
          <h2 className={styles.title}>{isEdit ? 'تعديل إعلان' : 'إنشاء إعلان'}</h2>
          <p className={styles.hint}>
            يُكتب محتوى الإعلان بالعربية، ويتولى الموقع ترجمته تلقائيًا عند تبديل
            اللغة — كما هو الحال في الأخبار والبرامج وبقية محتوى الموقع.
          </p>
        </div>
      </div>

      {/* ───────────────────────────────────────────────────────────────────
          «تعبئة سريعة» — HIDDEN 2026-10-07 by request.

          NOTHING WAS DELETED. `QUICK_FILL` and `applyQuickFill` above are
          intact, so restoring the panel is un-commenting this block and
          nothing else. Same treatment the CMS sidebar gives its hidden
          «التواصل» group.
          ─────────────────────────────────────────────────────────────────── */}
      {false && !isEdit && (
        <section className={styles.quickFill}>
          <div className={styles.quickFillHead}>
            <h3 className={styles.quickFillTitle}>تعبئة سريعة</h3>
            <p className={styles.fieldHint}>
              أمثلة جاهزة تملأ الحقول فورًا — غيّر ما تشاء قبل الحفظ. المثالان
              اللذان يتضمنان صورة يتركان حقل الصورة فارغًا لترفع صورتك، وأي صورة
              رفعتها لن تُمسح عند اختيار مثال آخر.
            </p>
          </div>

          <div className={styles.quickFillRow}>
            {QUICK_FILL.map((preset) => (
              <button
                key={preset.key}
                type='button'
                className={styles.quickFillButton}
                onClick={() => applyQuickFill(preset)}
              >
                <span className={styles.quickFillLabel}>{preset.label}</span>
                <span className={styles.quickFillNote}>{preset.note}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      <form className={styles.form} onSubmit={handleSubmit} noValidate dir='rtl'>
        {serverError.message && (
          <p className={styles.formError} role='alert'>
            {serverError.message}
          </p>
        )}

        {/* ------------------------------------------------------ content --- */}
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>المحتوى</h3>

          <div className={styles.grid}>
            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor='ann-layout'>
                التنسيق
              </label>
              <select
                id='ann-layout'
                className={styles.select}
                value={values.layout}
                onChange={(e) => setField({ layout: e.target.value })}
              >
                {LAYOUTS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              <p className={styles.fieldHint}>
                يحدد ما يظهر في النافذة: نص، أو صورة، أو الاثنان معًا.
              </p>
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor='ann-priority'>
                الأولوية
              </label>
              <input
                id='ann-priority'
                type='number'
                min={LIMITS.priorityMin}
                max={LIMITS.priorityMax}
                className={`${styles.input} ${
                  errorFor('priority') ? styles.inputInvalid : ''
                }`}
                value={values.priority}
                onChange={(e) => setField({ priority: e.target.value })}
              />
              {errorFor('priority') && (
                <p className={styles.fieldError} role='alert'>
                  {errorFor('priority')}
                </p>
              )}
              <p className={styles.fieldHint}>
                عند وجود أكثر من إعلان مباشر في الوقت نفسه، يُعرض صاحب الرقم
                الأعلى. يظهر إعلان واحد فقط للزائر.
              </p>
            </div>

            <div className={`${styles.field} ${styles.fieldWide}`}>
              <label className={styles.fieldLabel} htmlFor='ann-title'>
                العنوان
                {needsTitle && (
                  <span className={styles.required} aria-hidden='true'>
                    *
                  </span>
                )}
              </label>
              <input
                id='ann-title'
                type='text'
                maxLength={LIMITS.title}
                className={`${styles.input} ${
                  errorFor('title') ? styles.inputInvalid : ''
                }`}
                value={values.title}
                onChange={(e) => setField({ title: e.target.value })}
                aria-invalid={!!errorFor('title')}
                aria-describedby={errorFor('title') ? 'ann-title-error' : undefined}
              />
              {errorFor('title') && (
                <p className={styles.fieldError} id='ann-title-error' role='alert'>
                  {errorFor('title')}
                </p>
              )}
            </div>

            {values.layout !== 'ImageOnly' && (
              <div className={`${styles.field} ${styles.fieldWide}`}>
                <span className={styles.fieldLabel}>النص</span>
                {/* The wrapper exists only so the stylesheet can reach
                    Quill's own `.ql-editor`, which it renders itself and which
                    defaults to LTR — Arabic typed into it started at the left
                    edge and punctuation landed on the wrong side. */}
                <div className={styles.editor}>
                  <ReactQuill
                    value={values.body}
                    onChange={(html) => setField({ body: isBlankHtml(html) ? '' : html })}
                    modules={BODY_MODULES}
                    formats={BODY_FORMATS}
                    placeholder='نص الإعلان…'
                  />
                </div>
                <p className={styles.fieldHint}>
                  أبقِ النص قصيرًا — النافذة المنبثقة تُقرأ على الهاتف أيضًا.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* -------------------------------------------------------- image --- */}
        {needsImage && (
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>الصورة</h3>

            <div className={styles.imageRow}>
              <div className={styles.imagePreview}>
                {values.imageUrl ? (
                  <img src={values.imageUrl} alt='' />
                ) : (
                  <span className={styles.muted}>لا توجد صورة</span>
                )}
              </div>

              <div className={styles.imageActions}>
                <input
                  ref={fileInputRef}
                  type='file'
                  accept='image/jpeg,image/png,image/webp'
                  style={{ display: 'none' }}
                  onChange={handleFile}
                />
                {isUploading ? (
                  <LoaderButton />
                ) : (
                  <>
                    <button
                      type='button'
                      className={styles.ghostButton}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {values.imageUrl ? 'استبدال الصورة' : 'رفع صورة'}
                    </button>
                    {values.imageUrl && (
                      <button
                        type='button'
                        className={`${styles.linkButton} ${styles.danger}`}
                        onClick={() => setField({ imageUrl: '', imageAlt: '' })}
                      >
                        إزالة الصورة
                      </button>
                    )}
                  </>
                )}
                <p className={styles.fieldHint}>
                  JPG أو PNG أو WebP، بحد أقصى 2 ميجابايت. تُعرض الصورة كاملة دون
                  قص، لذا يُفضَّل نسبة 16:9.
                </p>
              </div>
            </div>

            {errorFor('imageUrl') && (
              <p className={styles.fieldError} role='alert'>
                {errorFor('imageUrl')}
              </p>
            )}

            {/* The translate service reads DOM text, so alt text translates —
                the words drawn INSIDE the image do not. */}
            <p className={styles.notice}>
              أي نص مكتوب داخل الصورة لن يُترجم عند تبديل لغة الموقع، لأنه جزء من
              الصورة. ضع العبارات المهمة في العنوان أو النص.
            </p>

            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor='ann-alt'>
                النص البديل للصورة
                <span className={styles.required} aria-hidden='true'>
                  *
                </span>
              </label>
              <input
                id='ann-alt'
                type='text'
                maxLength={LIMITS.imageAlt}
                className={`${styles.input} ${
                  errorFor('imageAlt') ? styles.inputInvalid : ''
                }`}
                value={values.imageAlt}
                onChange={(e) => setField({ imageAlt: e.target.value })}
                aria-invalid={!!errorFor('imageAlt')}
              />
              {errorFor('imageAlt') ? (
                <p className={styles.fieldError} role='alert'>
                  {errorFor('imageAlt')}
                </p>
              ) : (
                <p className={styles.fieldHint}>
                  وصف موجز لمحتوى الصورة، يقرأه برنامج قارئ الشاشة ويظهر إذا تعذّر
                  تحميل الصورة.
                </p>
              )}
            </div>
          </section>
        )}

        {/* ---------------------------------------------------------- CTA --- */}
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>زر الإجراء (اختياري)</h3>

          <div className={styles.grid}>
            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor='ann-cta-label'>
                نص الزر
              </label>
              <input
                id='ann-cta-label'
                type='text'
                maxLength={LIMITS.ctaLabel}
                className={`${styles.input} ${
                  errorFor('ctaLabel') ? styles.inputInvalid : ''
                }`}
                value={values.ctaLabel}
                onChange={(e) => setField({ ctaLabel: e.target.value })}
              />
              {errorFor('ctaLabel') ? (
                <p className={styles.fieldError} role='alert'>
                  {errorFor('ctaLabel')}
                </p>
              ) : values.layout === 'ImageOnly' ? (
                /* Only true for ImageOnly, and worth saying: it is the one
                   layout where a bare link is a complete, supported design. */
                <p className={styles.fieldHint}>
                  اتركه فارغًا ليصبح الإعلان كله (الصورة) قابلاً للنقر.
                </p>
              ) : null}
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor='ann-cta-url'>
                الرابط
              </label>
              <input
                id='ann-cta-url'
                type='text'
                dir='ltr'
                maxLength={LIMITS.ctaUrl}
                className={`${styles.input} ${
                  errorFor('ctaUrl') ? styles.inputInvalid : ''
                }`}
                value={values.ctaUrl}
                onChange={(e) => setField({ ctaUrl: e.target.value })}
                placeholder='/academic-calendar'
              />
              {errorFor('ctaUrl') ? (
                <p className={styles.fieldError} role='alert'>
                  {errorFor('ctaUrl')}
                </p>
              ) : (
                <p className={styles.fieldHint}>
                  لصفحة داخل الموقع اكتب المسار فقط، مثل <code>/academic-calendar</code>.
                </p>
              )}
            </div>

            <label className={`${styles.checkField} ${styles.fieldWide}`}>
              <input
                type='checkbox'
                className={styles.switch}
                checked={values.ctaOpensInNewTab}
                onChange={(e) => setField({ ctaOpensInNewTab: e.target.checked })}
              />
              <span className={styles.checkText}>
                <span className={styles.checkTitle}>فتح الرابط في تبويب جديد</span>
                <span className={styles.fieldHint}>
                  يُنصح بتفعيله للروابط الخارجية فقط، حتى لا يفقد الزائر الصفحة
                  التي كان يتصفحها.
                </span>
              </span>
            </label>
          </div>
        </section>

        {/* --------------------------------------------------- scheduling --- */}
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>الجدولة والتكرار</h3>

          <p className={styles.notice}>
            يُحتسب وقت البدء والانتهاء على خادم الموقع، لا على جهاز الزائر — فلا
            يتأثر ظهور الإعلان بإعدادات ساعة الزائر. الأوقات أدناه بتوقيتك المحلي.
          </p>

          <div className={styles.grid}>
            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor='ann-starts'>
                يبدأ في
                <span className={styles.required} aria-hidden='true'>
                  *
                </span>
              </label>
              <input
                id='ann-starts'
                type='datetime-local'
                className={`${styles.input} ${
                  errorFor('startsOn') ? styles.inputInvalid : ''
                }`}
                value={values.startsOn}
                onChange={(e) => setField({ startsOn: e.target.value })}
              />
              {errorFor('startsOn') && (
                <p className={styles.fieldError} role='alert'>
                  {errorFor('startsOn')}
                </p>
              )}
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor='ann-ends'>
                ينتهي في
              </label>
              <input
                id='ann-ends'
                type='datetime-local'
                className={`${styles.input} ${
                  errorFor('endsOn') ? styles.inputInvalid : ''
                }`}
                value={values.endsOn}
                onChange={(e) => setField({ endsOn: e.target.value })}
              />
              {errorFor('endsOn') ? (
                <p className={styles.fieldError} role='alert'>
                  {errorFor('endsOn')}
                </p>
              ) : (
                <p className={styles.fieldHint}>
                  اتركه فارغًا ليبقى الإعلان بلا تاريخ انتهاء.
                </p>
              )}
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor='ann-frequency'>
                تكرار الظهور
              </label>
              <select
                id='ann-frequency'
                className={styles.select}
                value={values.displayFrequency}
                onChange={(e) => setField({ displayFrequency: e.target.value })}
              >
                {FREQUENCIES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              <p className={styles.fieldHint}>{selectedFrequency?.hint}</p>
            </div>

            {values.displayFrequency === 'EveryNDays' && (
              <div className={styles.field}>
                <label className={styles.fieldLabel} htmlFor='ann-days'>
                  يعود للظهور بعد (أيام)
                  <span className={styles.required} aria-hidden='true'>
                    *
                  </span>
                </label>
                <input
                  id='ann-days'
                  type='number'
                  min={LIMITS.frequencyDaysMin}
                  max={LIMITS.frequencyDaysMax}
                  className={`${styles.input} ${
                    errorFor('frequencyDays') ? styles.inputInvalid : ''
                  }`}
                  value={values.frequencyDays}
                  onChange={(e) => setField({ frequencyDays: e.target.value })}
                />
                {errorFor('frequencyDays') && (
                  <p className={styles.fieldError} role='alert'>
                    {errorFor('frequencyDays')}
                  </p>
                )}
              </div>
            )}
          </div>

          <label className={styles.checkField}>
            <input
              type='checkbox'
              className={styles.switch}
              checked={values.isPublished}
              onChange={(e) => setField({ isPublished: e.target.checked })}
            />
            <span className={styles.checkText}>
              <span className={styles.checkTitle}>منشور</span>
              <span className={styles.fieldHint}>
                الإعلان المنشور يظهر للزوار ضمن المدة المحددة أعلاه فقط. قبل وقت
                البدء تكون حالته «مجدول»، وبعد الانتهاء «منتهٍ».
              </span>
            </span>
          </label>
        </section>

        {/* The one consequence of this save that an operator cannot undo and
            would not otherwise predict. Shown before saving, not after. */}
        {willResetDismissals && (
          <p className={styles.notice} role='status'>
            لقد عدّلت محتوى الإعلان، لذلك سيظهر من جديد لكل زائر كان قد أغلقه
            سابقًا.
          </p>
        )}

        <div className={styles.footActions}>
          {isSaving ? (
            <LoaderButton />
          ) : (
            <>
              {/* `type='submit'` is not decoration: the atom's other branch
                  wraps the button in a react-router `<Link>`, which inside a
                  form both navigates and submits. */}
              <Buttons
                text={isEdit ? 'حفظ التعديلات' : 'إنشاء الإعلان'}
                rounded
                color='rgba(13, 160, 110, 1)'
                type='submit'
                width={200}
                style={{ flex: '0 0 auto' }}
              />
              <button
                type='button'
                className={styles.ghostButton}
                onClick={() => navigate('/home/announcements')}
              >
                إلغاء
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default AnnouncementForm;
